import { AnalyticsService } from "./service";

/**
 * Tracks timing metrics throughout the checkout flow.
 *
 * Create one instance per checkout session and call the track methods
 * at appropriate points in the checkout flow.
 *
 * Usage:
 *   const requestId = getOrCreateCheckoutRequestId(environment);
 *   const tracker = new CheckoutTimingTracker(secureToken, requestId);
 *   tracker.trackPageReady();
 *   // ... iframe loads ...
 *   tracker.trackInputReady();
 *   // ... user submits ...
 *   tracker.trackSubmit();
 *   // ... API response ...
 *   tracker.trackSuccess(attemptId);
 */
export class CheckoutTimingTracker {
  // Class-level guard to prevent duplicate page_ready events across PaymentKit instances
  private static pageReadyTracked = false;

  private startTime: number;
  private checkoutSessionId: string;
  private requestId: string;
  private inputReadyTracked = false;

  constructor(checkoutSessionId: string, requestId: string) {
    this.startTime = performance.now();
    this.checkoutSessionId = checkoutSessionId;
    this.requestId = requestId;
  }

  /**
   * Track when SDK is initialized and ready.
   * Call immediately after PaymentKit() initialization.
   * Only tracks once per page load (subsequent calls are no-ops).
   */
  trackPageReady(): void {
    if (CheckoutTimingTracker.pageReadyTracked) return;
    CheckoutTimingTracker.pageReadyTracked = true;

    AnalyticsService.capture("checkout_page_ready", {
      checkout_session_id: this.checkoutSessionId,
      request_id: this.requestId,
      elapsed_ms: this.getElapsedMs(),
    });
  }

  /**
   * Track when card input iframe is loaded and ready for input.
   * Should only be called once (for first iframe, typically card_pan).
   * Subsequent calls are no-ops.
   */
  trackInputReady(): void {
    if (this.inputReadyTracked) return;
    this.inputReadyTracked = true;

    AnalyticsService.capture("checkout_input_ready", {
      checkout_session_id: this.checkoutSessionId,
      request_id: this.requestId,
      elapsed_ms: this.getElapsedMs(),
    });
  }

  /**
   * Track when user clicks submit button.
   */
  trackSubmit(): void {
    AnalyticsService.capture("checkout_submit", {
      checkout_session_id: this.checkoutSessionId,
      request_id: this.requestId,
      elapsed_ms: this.getElapsedMs(),
    });
  }

  /**
   * Track successful checkout.
   *
   * @param checkoutAttemptId - The checkout attempt ID
   */
  trackSuccess(checkoutAttemptId: string): void {
    AnalyticsService.capture("checkout_success", {
      checkout_session_id: this.checkoutSessionId,
      checkout_attempt_id: checkoutAttemptId,
      request_id: this.requestId,
      total_elapsed_ms: this.getElapsedMs(),
    });
  }

  /**
   * Track failed checkout.
   *
   * @param checkoutAttemptId - The checkout attempt ID (may be null if failed early)
   * @param errorCode - The error code from the checkout response
   * @param errorMessage - The customer-facing error message
   */
  trackFail(checkoutAttemptId: string | null, errorCode: string | null, errorMessage: string | null): void {
    AnalyticsService.capture("checkout_fail", {
      checkout_session_id: this.checkoutSessionId,
      checkout_attempt_id: checkoutAttemptId,
      request_id: this.requestId,
      error_code: errorCode,
      error_message: errorMessage,
      total_elapsed_ms: this.getElapsedMs(),
    });
  }

  /**
   * Get elapsed time in milliseconds since tracker creation.
   */
  private getElapsedMs(): number {
    return Math.round(performance.now() - this.startTime);
  }
}
